import { HttpClient } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApiAuthService } from './api-auth.service';
import { UsersApiService } from './users-api.service';
import type { User } from '../models/user.model';

export type { User } from '../models/user.model';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  belt: string;
}

@Service()
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly usersApi = inject(UsersApiService);
  private readonly apiAuthService = inject(ApiAuthService);

  private readonly currentUserSignal = signal<User | null>(null);

  readonly currentUser = this.currentUserSignal.asReadonly();
  // ponytail: single source of truth (F1) — isAuthenticated delegates to the
  // BFF token state, eliminating the localStorage/token desync.
  readonly isAuthenticated = computed(() => this.apiAuthService.isAuthenticated());

  login(credentials: LoginCredentials): Observable<User> {
    return this.http
      .post<{ userId: string; email: string; name: string; belt?: string }>(
        `${environment.api.baseUrl}/auth/login`,
        credentials,
        { withCredentials: true },
      )
      .pipe(
        map((res): User => {
          const user: User = { guid: res.userId, email: res.email, name: res.name };
          if (res.belt) user.belt = res.belt;
          return user;
        }),
        tap((user) => this.currentUserSignal.set(user)),
        catchError((error) => {
          this.clearAuthState();
          return throwError(() => error);
        }),
      );
  }

  logout(): Observable<void> {
    const revokeBff$ = this.apiAuthService.clearToken().pipe(catchError(() => of(void 0)));
    const clearLoginCookie$ = this.http
      .post<void>(`${environment.api.baseUrl}/auth/logout`, null, { withCredentials: true })
      .pipe(catchError(() => of(void 0)));

    return forkJoin([revokeBff$, clearLoginCookie$]).pipe(
      map(() => void 0),
      finalize(() => this.clearAuthState()),
    );
  }

  register(data: RegisterData): Observable<{ guid: string }> {
    return this.usersApi
      .registerUser({
        name: data.username,
        surname: '',
        email: data.email,
        password: data.password,
        username: data.username,
        belt: data.belt,
      })
      .pipe(
        catchError((error) => {
          console.error('Registration failed:', error);
          return throwError(() => error);
        }),
      );
  }

  updateCurrentUser(updates: { name?: string; belt?: string }): Observable<User> {
    const currentUser = this.currentUserSignal();
    if (!currentUser) {
      return throwError(() => new Error('No user logged in'));
    }
    return this.usersApi.updateMe(updates).pipe(
      tap((updatedUser) => {
        const user: User = {
          guid: updatedUser.guid,
          name: updatedUser.name,
          email: updatedUser.email,
        };
        if (updatedUser.surname) user.surname = updatedUser.surname;
        if (updatedUser.username) user.username = updatedUser.username;
        if (updatedUser.belt) user.belt = updatedUser.belt;
        this.currentUserSignal.set(user);
      }),
      catchError((error) => {
        console.error('Profile update failed:', error);
        return throwError(() => error);
      }),
    );
  }

  /** Called by the auth initializer after a successful session restore. */
  setCurrentUser(user: User): void {
    this.currentUserSignal.set(user);
  }

  clearAuthState(): void {
    this.currentUserSignal.set(null);
  }
}
